/**
 * The coalition graph. Position is the hue *and* the shape of a node, influence
 * on this issue is its size, and the ties that cross between camps are the only
 * bright lines — they are the routes a message can actually travel.
 *
 * Force layout, camera flights and the hover card follow
 * `features/intelligence/network/NetworkCanvas`; the layout helpers are shared
 * with it rather than rewritten.
 */

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { Maximize2, Minus, Plus } from 'lucide-react';
import { IconButton } from '../../../components/ui';
import {
  Region,
  View,
  clusterRegions,
  easeInOutCubic,
  fitView,
  flightPath,
  forceCollideGrid,
  forceTarget,
  paddedHull,
} from '../../intelligence/network/layout';
import type { MapGraphNode, MapModel } from './mapModel';
import { blocLabel } from './mapModel';
import { isUnknown, labelOf, positionStyle } from './position';

const FONT = 'Geist, system-ui, sans-serif';
const CROSS_HEX = '#f59e0b'; // the one warm line: a tie between camps
const SELECT_HEX = '#ea2a33';

type Lens = 'blocs' | 'network';

interface SimNode {
  id: string;
  data: MapGraphNode;
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
  fx?: number;
  fy?: number;
  rDraw: number;
}

interface SimLink {
  id: string;
  source: string | SimNode;
  target: string | SimNode;
  cross: boolean;
  weight: number;
  kind: string;
  label: string | null;
}

export interface MapCanvasHandle {
  flyTo: (id: string, zoom?: number) => void;
  fit: (ids?: string[]) => void;
  zoomBy: (factor: number) => void;
}

export interface MapCanvasProps {
  model: MapModel;
  lens: Lens;
  selectedId: string | null;
  /** Hover coming from the lists above, so a row and a node light up together */
  hoverId: string | null;
  showLabels: boolean;
  reducedMotion?: boolean;
  onSelect: (id: string | null) => void;
  className?: string;
}

const rgba = (hex: string, alpha: number) => {
  const n = parseInt(hex.replace('#', ''), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${Math.max(0, Math.min(1, alpha)).toFixed(3)})`;
};

/**
 * The positions board marks strength with chevrons (double up = champion, a bar
 * = neutral, double down = strongly opposed). The same marks are cut into the
 * nodes here, so position survives greyscale and the two views read alike.
 */
function drawChevrons(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, position: number, alpha: number) {
  const step = Math.max(-2, Math.min(2, Math.round(position)));
  const w = r * 0.46;
  const h = r * 0.2;
  ctx.strokeStyle = `rgba(255,255,255,${0.95 * alpha})`;
  ctx.lineWidth = Math.max(0.7, r * 0.17);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (step === 0) {
    ctx.beginPath();
    ctx.moveTo(x - w, y);
    ctx.lineTo(x + w, y);
    ctx.stroke();
    return;
  }
  const dir = step > 0 ? 1 : -1;
  const chevron = (cy: number) => {
    ctx.beginPath();
    ctx.moveTo(x - w, cy + dir * h);
    ctx.lineTo(x, cy - dir * h);
    ctx.lineTo(x + w, cy + dir * h);
    ctx.stroke();
  };
  if (Math.abs(step) === 1) chevron(y);
  else {
    const gap = r * 0.24;
    chevron(y - gap);
    chevron(y + gap);
  }
}

export const MapCanvas = forwardRef<MapCanvasHandle, MapCanvasProps>(function MapCanvas(props, ref) {
  const { model, lens, selectedId, hoverId, showLabels } = props;
  const fgRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ width: 720, height: 460 });
  const [canvasHover, setCanvasHover] = useState<string | null>(null);
  const propsRef = useRef(props);
  propsRef.current = props;
  const simNodes = useRef(new Map<string, SimNode>());
  const fitOnSettle = useRef(true);

  /* ---------- sizing ---------- */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        setDims((d) => {
          const width = Math.max(200, Math.floor(rect.width));
          const height = Math.max(200, Math.floor(rect.height));
          return d.width === width && d.height === height ? d : { width, height };
        });
      });
    };
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, []);

  /* ---------- graph data (positions survive re-renders) ---------- */
  const graphData = useMemo(() => {
    const kept = new Set<string>();
    const nodes = [...model.nodes]
      .sort((a, b) => b.influence - a.influence)
      .map((data) => {
        const existing = simNodes.current.get(data.id);
        const node: SimNode = existing || { id: data.id, data, rDraw: data.radius };
        node.data = data;
        node.rDraw = data.radius;
        simNodes.current.set(data.id, node);
        kept.add(data.id);
        return node;
      });
    simNodes.current.forEach((_, id) => {
      if (!kept.has(id)) simNodes.current.delete(id);
    });
    const links: SimLink[] = model.links.map((link) => ({
      id: link.id,
      source: link.source,
      target: link.target,
      cross: link.crossesCamps,
      weight: Number.isFinite(link.edge.weight) ? link.edge.weight : 1,
      kind: link.edge.kind,
      label: link.edge.label,
    }));
    return { nodes, links };
  }, [model]);

  /* ---------- bloc regions ---------- */
  const regions = useMemo(() => {
    if (lens !== 'blocs' || model.blocs.length === 0) return null;
    const groups = model.blocs.map((b) => ({ key: b.id, size: Math.max(1, b.memberIds.length) }));
    const loose = model.nodes.filter((n) => n.bloc === null || n.bloc === undefined).length;
    if (loose) groups.push({ key: -1, size: Math.max(2, Math.round(loose * 0.7)) });
    // clusterRegions reserves room for a full cluster; these blocs are small, so
    // pull the whole arrangement in — otherwise the board is mostly white space
    const packed = clusterRegions(groups, 24);
    const tightened = new Map<number, Region>();
    packed.forEach((region, key) => tightened.set(key, { x: region.x * 0.6, y: region.y * 0.6, r: region.r }));
    return tightened;
  }, [lens, model]);

  const regionOf = useCallback(
    (id: string): Region | null => {
      if (!regions) return null;
      const node = model.byId.get(id);
      const bloc = node?.bloc;
      return regions.get(bloc === null || bloc === undefined ? -1 : bloc) || regions.get(-1) || null;
    },
    [regions, model],
  );

  /* ---------- camera ---------- */
  const flight = useRef(0);
  const flyToView = useCallback(
    (view: View | null) => {
      const fg = fgRef.current;
      if (!fg || !view) return;
      cancelAnimationFrame(flight.current);
      if (propsRef.current.reducedMotion) {
        fg.zoom(view.k);
        fg.centerAt(view.x, view.y);
        return;
      }
      const centre = fg.centerAt();
      const from: View = { x: centre.x, y: centre.y, k: fg.zoom() };
      const { at, duration } = flightPath(from, view, dims.width);
      const start = performance.now();
      const step = () => {
        const t = Math.min(1, (performance.now() - start) / duration);
        const v = at(easeInOutCubic(t));
        fg.zoom(v.k);
        fg.centerAt(v.x, v.y);
        if (t < 1) flight.current = requestAnimationFrame(step);
      };
      flight.current = requestAnimationFrame(step);
    },
    [dims.width],
  );
  useEffect(() => () => cancelAnimationFrame(flight.current), []);

  const pointsOf = useCallback((ids?: string[]) => {
    const source = ids?.length ? ids.map((id) => simNodes.current.get(id)) : Array.from(simNodes.current.values());
    return source
      .filter((n): n is SimNode => !!n && n.x !== undefined)
      .map((n) => ({ x: n.x!, y: n.y!, r: n.rDraw + 16 }));
  }, []);

  const fitAll = useCallback(
    (ids?: string[]) => {
      const points = pointsOf(ids);
      if (!points.length) return;
      flyToView(fitView(points, dims.width, dims.height, 32, ids?.length === 1 ? 2.8 : 2.6));
    },
    [pointsOf, flyToView, dims],
  );

  useImperativeHandle(
    ref,
    () => ({
      flyTo: (id, zoom) => {
        const n = simNodes.current.get(id);
        const fg = fgRef.current;
        if (!n || n.x === undefined || !fg) return;
        fitOnSettle.current = false;
        flyToView({ x: n.x, y: n.y!, k: Math.max(fg.zoom(), zoom ?? 1.8) });
      },
      fit: (ids) => {
        fitOnSettle.current = false;
        fitAll(ids);
      },
      zoomBy: (factor) => {
        const fg = fgRef.current;
        if (!fg) return;
        fg.zoom(fg.zoom() * factor, propsRef.current.reducedMotion ? 0 : 240);
      },
    }),
    [flyToView, fitAll],
  );

  /* ---------- forces ---------- */
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    const charge = fg.d3Force('charge');
    const link = fg.d3Force('link');
    const count = graphData.nodes.length;
    link?.distance((l: SimLink) => (l.cross ? 110 : 70)).strength((l: SimLink) => (l.cross ? 0.05 : 0.35));
    fg.d3Force('y', null);
    if (lens === 'blocs' && regions) {
      charge?.strength(-52).distanceMax(240);
      fg.d3Force(
        'x',
        forceTarget<SimNode>((n) => regionOf(n.id), () => 0.17),
      );
      fg.d3Force('collide', forceCollideGrid<SimNode>((n) => n.rDraw + 4, 0.8));
    } else {
      charge?.strength(count > 300 ? -80 : count > 120 ? -130 : -200).distanceMax(560);
      fg.d3Force(
        'x',
        forceTarget<SimNode>(() => ({ x: 0, y: 0 }), () => 0.05),
      );
      fg.d3Force('collide', forceCollideGrid<SimNode>((n) => n.rDraw + 8, 0.7));
    }
    fg.d3ReheatSimulation?.();
    fitOnSettle.current = true;
  }, [lens, regions, regionOf, graphData]);

  /* ---------- what is emphasised ---------- */
  const focusId = selectedId || hoverId || canvasHover;
  const focusSet = useMemo(() => {
    if (!focusId) return null;
    const set = new Set<string>([focusId]);
    (model.neighbours.get(focusId) || []).forEach((n) => set.add(n.id));
    return set;
  }, [focusId, model]);

  /** Only the heaviest actors earn a permanent name; the rest appear on hover */
  const named = useMemo(() => {
    return new Set(
      [...model.nodes]
        .sort((a, b) => b.influence - a.influence)
        .slice(0, 10)
        .map((n) => n.id),
    );
  }, [model]);

  const emphasis = useRef({ focusSet, focusId, showLabels, model, named });
  emphasis.current = { focusSet, focusId, showLabels, model, named };

  /* ---------- hulls (throttled: a convex hull per frame is wasted work) ---------- */
  /** Boxes already claimed by a label this frame, so names never pile up */
  const labelBoxes = useRef<Array<[number, number, number, number]>>([]);
  const claim = (box: [number, number, number, number]): boolean => {
    const clash = labelBoxes.current.some((b) => !(box[2] < b[0] || box[0] > b[2] || box[3] < b[1] || box[1] > b[3]));
    if (clash) return false;
    labelBoxes.current.push(box);
    return true;
  };

  const hulls = useRef<
    Array<{ id: number; points: Array<[number, number]>; hex: string; label: string; cx: number; cy: number; top: number; width: number }>
  >([]);
  const hullAt = useRef(0);

  const onRenderFramePre = useCallback(
    (ctx: CanvasRenderingContext2D, scale: number) => {
      labelBoxes.current = [];
      if (propsRef.current.lens !== 'blocs') {
        hulls.current = [];
        return;
      }
      const now = performance.now();
      if (now - hullAt.current > 140) {
        hullAt.current = now;
        hulls.current = propsRef.current.model.blocs
          .map((bloc) => {
            const circles = bloc.memberIds
              .map((id) => simNodes.current.get(id))
              .filter((n): n is SimNode => !!n && n.x !== undefined)
              .map((n) => ({ x: n.x!, y: n.y!, r: n.rDraw + 14 }));
            if (circles.length < 2) return null;
            const points = paddedHull(circles, 6, 8);
            if (points.length < 3) return null;
            let cx = 0;
            let cy = 0;
            let top = Infinity;
            let left = Infinity;
            let right = -Infinity;
            points.forEach(([x, y]) => {
              cx += x;
              cy += y;
              top = Math.min(top, y);
              left = Math.min(left, x);
              right = Math.max(right, x);
            });
            const label = blocLabel(bloc);
            return {
              id: bloc.id,
              points,
              hex: positionStyle(bloc.meanPosition).hex,
              label: label.length > 40 ? `${label.slice(0, 39)}…` : label,
              cx: cx / points.length,
              cy: cy / points.length,
              top,
              width: right - left,
            };
          })
          .filter((h): h is NonNullable<typeof h> => !!h);
      }

      hulls.current.forEach((hull) => {
        ctx.beginPath();
        hull.points.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
        ctx.closePath();
        ctx.fillStyle = rgba(hull.hex, 0.05);
        ctx.fill();
        ctx.strokeStyle = rgba(hull.hex, 0.22);
        ctx.lineWidth = 1 / scale;
        ctx.stroke();
        // a name nobody can read is just noise: wait until the bloc owns some room
        if (hull.width * scale > 70) {
          const size = Math.max(9, 11 / scale);
          ctx.font = `${size}px ${FONT}`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          const w = ctx.measureText(hull.label).width;
          const y = hull.top - 6 / scale;
          // Centred over the bloc but kept inside the visible canvas: on a phone a bloc near the edge
          // is narrower than its name, which ran off the card
          const view = ctx.getTransform();
          const viewLeft = -view.e / view.a;
          const viewRight = (ctx.canvas.width - view.e) / view.a;
          const half = w / 2 + 4 / scale;
          const x = viewRight - viewLeft > half * 2 ? Math.min(Math.max(hull.cx, viewLeft + half), viewRight - half) : hull.cx;
          if (claim([x - w / 2, y - size, x + w / 2, y])) {
            ctx.fillStyle = 'rgba(255,255,255,0.82)';
            ctx.fillRect(x - w / 2 - 2 / scale, y - size, w + 4 / scale, size + 2 / scale);
            ctx.fillStyle = 'rgba(82,82,82,0.9)';
            ctx.fillText(hull.label, x, y);
          }
        }
      });
    },
    [],
  );

  /* ---------- painting ---------- */
  const paintLink = useCallback((link: SimLink, ctx: CanvasRenderingContext2D, scale: number) => {
    const a = link.source as SimNode;
    const b = link.target as SimNode;
    if (typeof a !== 'object' || typeof b !== 'object' || a.x === undefined || b.x === undefined) return;
    const { focusSet } = emphasis.current;
    const related = !focusSet || (focusSet.has(a.id) && focusSet.has(b.id));
    const dim = focusSet && !related;
    ctx.beginPath();
    ctx.moveTo(a.x!, a.y!);
    ctx.lineTo(b.x!, b.y!);
    if (link.cross) {
      ctx.strokeStyle = rgba(CROSS_HEX, dim ? 0.12 : 0.75);
      ctx.lineWidth = (dim ? 0.9 : 1.7) / scale;
    } else {
      ctx.strokeStyle = `rgba(163,163,163,${dim ? 0.07 : 0.28})`;
      ctx.lineWidth = 1 / scale;
    }
    ctx.stroke();
  }, []);

  const paintNode = useCallback((node: SimNode, ctx: CanvasRenderingContext2D, scale: number) => {
    if (node.x === undefined) return;
    const { focusSet, focusId: focus, showLabels: labels } = emphasis.current;
    const data = node.data;
    const position = data.node.position;
    const unknown = isUnknown(position);
    const hex = unknown ? '#ffffff' : positionStyle(position as number).hex;
    const r = node.rDraw;
    const selected = propsRef.current.selectedId === node.id;
    const focused = focus === node.id;
    const dim = focusSet && !focusSet.has(node.id);
    const alpha = dim ? 0.22 : 1;

    // swing: an amber halo, because an undecided heavyweight is the opportunity
    if (data.node.isSwing) {
      ctx.beginPath();
      ctx.arc(node.x, node.y!, r + 4.5, 0, Math.PI * 2);
      ctx.strokeStyle = rgba(CROSS_HEX, 0.5 * alpha);
      ctx.lineWidth = 1.2 / scale;
      ctx.setLineDash([2.5 / scale, 2 / scale]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    // bridge: a solid outer ring — they stand in both camps
    if (data.node.isBridge) {
      ctx.beginPath();
      ctx.arc(node.x, node.y!, r + 2.6, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(10,10,10,${0.55 * alpha})`;
      ctx.lineWidth = 1.1 / scale;
      ctx.stroke();
    }

    ctx.beginPath();
    ctx.arc(node.x, node.y!, r, 0, Math.PI * 2);
    if (unknown) {
      // nothing on record: an outline, so a blind spot never reads as a neutral
      ctx.fillStyle = `rgba(255,255,255,${alpha})`;
      ctx.fill();
      ctx.strokeStyle = `rgba(163,163,163,${0.85 * alpha})`;
      ctx.lineWidth = 1.3 / scale;
      ctx.setLineDash([3 / scale, 2.4 / scale]);
      ctx.stroke();
      ctx.setLineDash([]);
    } else {
      ctx.fillStyle = rgba(hex, alpha);
      ctx.fill();
      // the board's chevrons, drawn inside the node once there is room for them
      if (r * scale > 5.5) drawChevrons(ctx, node.x, node.y!, r, position as number, alpha);
    }

    if (selected || focused) {
      ctx.beginPath();
      ctx.arc(node.x, node.y!, r + 6, 0, Math.PI * 2);
      ctx.strokeStyle = rgba(SELECT_HEX, selected ? 0.9 : 0.35);
      ctx.lineWidth = (selected ? 1.8 : 1.2) / scale;
      ctx.stroke();
    }

    const bigEnough = r * scale > 7 && (emphasis.current.named.has(node.id) || r * scale > 13);
    if (!dim && (selected || focused || (labels && bigEnough))) {
      const size = Math.max(9, 11 / scale);
      ctx.font = `${size}px ${FONT}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      const text = data.label.length > 26 ? `${data.label.slice(0, 25)}…` : data.label;
      const width = ctx.measureText(text).width;
      const top = node.y! + r + 2.5 / scale;
      const box: [number, number, number, number] = [node.x - width / 2, top, node.x + width / 2, top + size];
      // a selection always gets its name; the rest give way to whoever got there first
      if (selected || focused || claim(box)) {
        ctx.fillStyle = 'rgba(255,255,255,0.82)';
        ctx.fillRect(node.x - width / 2 - 2 / scale, top, width + 4 / scale, size + 1 / scale);
        ctx.fillStyle = selected || focused ? '#0a0a0a' : 'rgba(64,64,64,0.95)';
        ctx.fillText(text, node.x, node.y! + r + 3 / scale);
      }
    }
  }, []);

  const handleEngineStop = useCallback(() => {
    if (!fitOnSettle.current) return;
    fitOnSettle.current = false;
    fitAll();
  }, [fitAll]);

  const hoverNode = canvasHover ? simNodes.current.get(canvasHover) : null;
  const hoverCard = (() => {
    if (!hoverNode || hoverNode.x === undefined || !fgRef.current?.graph2ScreenCoords) return null;
    const p = fgRef.current.graph2ScreenCoords(hoverNode.x, hoverNode.y);
    return { x: p.x, y: p.y, r: hoverNode.rDraw * (fgRef.current.zoom?.() ?? 1), data: hoverNode.data };
  })();

  const warmup = Math.min(110, Math.round(20000 / Math.max(40, graphData.nodes.length)));

  return (
    <div className={`absolute inset-0 overflow-hidden rounded-b-[15px] bg-white ${props.className || ''}`}>
      <div ref={containerRef} className="absolute inset-0" onMouseLeave={() => setCanvasHover(null)}>
        <ForceGraph2D
          ref={fgRef}
          graphData={graphData as any}
          width={dims.width}
          height={dims.height}
          backgroundColor="#ffffff"
          nodeLabel={() => ''}
          linkLabel={() => ''}
          nodeCanvasObjectMode={() => 'replace'}
          nodeCanvasObject={paintNode as any}
          nodePointerAreaPaint={(node: any, color: string, ctx: CanvasRenderingContext2D) => {
            if (node.x === undefined) return;
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.arc(node.x, node.y, (node.rDraw || 6) + 3, 0, Math.PI * 2);
            ctx.fill();
          }}
          linkCanvasObjectMode={() => 'replace'}
          linkCanvasObject={paintLink as any}
          onRenderFramePre={onRenderFramePre as any}
          onNodeClick={((node: SimNode) => props.onSelect(node.id)) as any}
          onNodeHover={((node: SimNode | null) => setCanvasHover(node?.id ?? null)) as any}
          onBackgroundClick={() => props.onSelect(null)}
          onEngineStop={handleEngineStop}
          cooldownTicks={Infinity}
          cooldownTime={6000}
          warmupTicks={warmup}
          d3AlphaMin={0.005}
          d3VelocityDecay={0.34}
          minZoom={0.1}
          maxZoom={7}
        />
      </div>

      {hoverCard && (
        <div
          className="pointer-events-none absolute z-10 w-[220px] animate-fade-up rounded-xl bg-white px-3 py-2.5 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.25)] ring-1 ring-black/[0.06] motion-reduce:animate-none"
          style={{
            left: Math.min(Math.max(8, hoverCard.x + 14), Math.max(8, dims.width - 232)),
            top: Math.min(Math.max(8, hoverCard.y + hoverCard.r + 8), Math.max(8, dims.height - 96)),
          }}
        >
          <p className="truncate text-[13px] leading-tight text-ink">{hoverCard.data.label}</p>
          <p className="mt-1 text-[11.5px] text-neutral-500">
            {labelOf(hoverCard.data.node.position)}
            {hoverCard.data.influence > 0 && ` · influence ${Math.round(hoverCard.data.influence * 100)}`}
          </p>
          {(hoverCard.data.node.isBridge || hoverCard.data.node.isSwing) && (
            <p className="mt-1 text-[11.5px] text-amber-600">
              {[hoverCard.data.node.isBridge && 'Bridge', hoverCard.data.node.isSwing && 'Swing'].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
      )}

      <div className="absolute bottom-3 right-3 z-10 flex flex-col gap-1 rounded-full bg-white/90 p-1 ring-1 ring-black/[0.06] backdrop-blur">
        <IconButton label="Zoom in" size="sm" onClick={() => fgRef.current?.zoom((fgRef.current?.zoom() || 1) * 1.35, 240)}>
          <Plus className="h-4 w-4" strokeWidth={1.75} />
        </IconButton>
        <IconButton label="Zoom out" size="sm" onClick={() => fgRef.current?.zoom((fgRef.current?.zoom() || 1) / 1.35, 240)}>
          <Minus className="h-4 w-4" strokeWidth={1.75} />
        </IconButton>
        <IconButton label="Fit to view" size="sm" onClick={() => fitAll()}>
          <Maximize2 className="h-4 w-4" strokeWidth={1.75} />
        </IconButton>
      </div>
    </div>
  );
});
